import { Button } from "flowbite-react";
import { Funnel, Check, ArrowCounterClockwise } from "@phosphor-icons/react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select, { SingleValue } from "react-select";
import Drawer from "./Drawer";
import DateRangePickerButton from "./DateRangePicker/DateRangePickerButton";
import { useIsMobile } from "../hooks";
import { customSelectStyles } from "../shared/constants";
import { IDateRangePreset, TDateRangePresetId } from "../interfaces";

export type DynamicFilterSelectOption = {
  label: string;
  value: string | number | null;
  // Allow extra metadata if needed by consumer
  [key: string]: unknown;
};

export type DynamicFilterDateRangeValue = {
  startDate: Date | undefined;
  endDate: Date | undefined;
  preset?: TDateRangePresetId;
};

export type DynamicFilterFieldBase = {
  id: string;
  label: string;
  helperText?: string;
  hidden?: boolean;
  // Initial applied value from parent (used to hydrate local buffer)
  value?: unknown;
  // Called when Apply is pressed (after local buffering). For immediate updates, you may also use onChange while editing.
  onChange?: (value: unknown) => void;
};

export type DynamicFilterField =
  | (DynamicFilterFieldBase & {
    type: "select";
    placeholder?: string;
    options: DynamicFilterSelectOption[];
    isClearable?: boolean;
    // Optional custom components for react-select (Option, SingleValue, etc.)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectComponents?: any;
  })
  | (DynamicFilterFieldBase & {
    type: "checkbox";
  })
  | (DynamicFilterFieldBase & {
    type: "text";
    placeholder?: string;
  })
  | (DynamicFilterFieldBase & {
    type: "number";
    placeholder?: string;
    min?: number;
    max?: number;
    step?: number;
  })
  | (DynamicFilterFieldBase & {
    type: "dateRange";
    // When provided via value, expect { startDate, endDate, preset? }
    onPresetChange?: (preset: IDateRangePreset | undefined) => void;
  });

export type DynamicFilterValues = Record<string, unknown>;

export type DynamicFilterProps = {
  title: string;
  fields: DynamicFilterField[];
  onApply: (values: DynamicFilterValues) => void;
  onCancel?: () => void;
  // Controls
  open?: boolean;
  setOpen?: (open: boolean) => void;
  // Optional trigger to open the drawer. If not provided, a small Filter button is rendered.
  trigger?: ReactNode;
  // i18n function. Fallback strings used when not provided.
  t?: (key: string) => string;
  // Drawer sizing overrides
  drawerSizeDesktop?: "sm" | "md" | "lg" | "full" | "half" | "1/3";
  drawerDirection?: "left" | "right" | "top" | "bottom";
};



export default function DynamicFilter({
  title,
  fields,
  onApply,
  onCancel,
  open,
  setOpen,
  trigger,
  t: propT,
  drawerSizeDesktop = "1/3",
  drawerDirection = "top",
}: DynamicFilterProps) {
  const { t: i18nT } = useTranslation();
  const t = propT || i18nT;
  const isMobile = useIsMobile();

  // Internal open/close state if not controlled
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const isControlled = typeof open === "boolean" && typeof setOpen === "function";
  const isOpen = isControlled ? Boolean(open) : internalOpen;
  const updateOpen = (v: boolean) => (isControlled ? setOpen?.(v) : setInternalOpen(v));

  // Build initial values map from field.value
  const initialValues = useMemo<DynamicFilterValues>(() => {
    const map: DynamicFilterValues = {};
    for (const f of fields) {
      if (f.hidden) continue;
      map[f.id] = f.value ?? getDefaultValueForField(f);
    }
    return map;
  }, [fields]);

  const [localValues, setLocalValues] = useState<DynamicFilterValues>(initialValues);

  // Sync local buffer when the drawer is opened or fields change
  useEffect(() => {
    if (isOpen) {
      setLocalValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialValues]);

  const handleFieldChange = (field: DynamicFilterField, value: unknown) => {
    setLocalValues((prev) => ({ ...prev, [field.id]: value }));
  };

  const handleApply = () => {
    // Call per-field onChange if provided
    fields.forEach((f) => {
      if (f.hidden) return;
      const v = localValues[f.id];
      f.onChange?.(v);
    });
    onApply(localValues);
    updateOpen(false);
  };

  const handleCancel = () => {
    setLocalValues(initialValues);
    onCancel?.();
    updateOpen(false);
  };

  const filterContent = (
    <div>
      <div className={`space-y-4  ${!isMobile ? "" : "mb-5"}`}>
        {fields.filter((f) => !f.hidden).map((field) => (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            {renderField(field, localValues[field.id], (v) => handleFieldChange(field, v))}
            {field.helperText && (
              <p className="mt-1 text-xs text-gray-500">{field.helperText}</p>
            )}
          </div>
        ))}
      </div>
      <div
        className={`flex items-center gap-2 bg-white ${isMobile ? "fixed bottom-0 left-0 right-0 p-4" : "pt-4"
          }`}
      >
        <Button color="light" size={"sm"} className="w-full" onClick={handleCancel}>
          <div className="flex items-center gap-2">
            <ArrowCounterClockwise size={16} />
            {t("common.cancel")}
          </div>
        </Button>
        <Button color="indigo" size={"sm"} className="w-full" onClick={handleApply}>
          <div className="flex items-center gap-2">
            <Check size={16} />
            {t("common.apply")}
          </div>
        </Button>
      </div>
    </div>
  );

  const defaultTrigger = (
    <Button color="light" onClick={() => updateOpen(true)} size={"sm"}>
      <div className="flex items-center gap-2">
        <Funnel size={16} />
        {t("common.filter")}
      </div>
    </Button>
  );

  return (
    <>
      {trigger ?? defaultTrigger}
      <Drawer
        show={isOpen}
        isMobile={isMobile}
        onClose={() => updateOpen(false)}
        title={title}
        size={isMobile ? "full" : drawerSizeDesktop}
        direction={drawerDirection}
      >
        {filterContent}
      </Drawer>
    </>
  );
}

function getDefaultValueForField(field: DynamicFilterField): unknown {
  switch (field.type) {
    case "checkbox":
      return Boolean(field.value ?? false);
    case "number":
      return field.value ?? undefined;
    case "text":
      return (field.value as string) ?? "";
    case "select":
      return (field.value as DynamicFilterSelectOption | null) ?? null;
    case "dateRange": {
      const v = field.value as DynamicFilterDateRangeValue | undefined;
      return (
        v ?? {
          startDate: undefined,
          endDate: undefined,
          preset: undefined,
        }
      );
    }
    default:
      return undefined;
  }
}

function renderField(
  field: DynamicFilterField,
  value: unknown,
  onChange: (value: unknown) => void
) {
  switch (field.type) {
    case "select":
      return (
        <Select<DynamicFilterSelectOption>
          classNamePrefix={"custom-select"}
          menuPlacement="auto"
          value={(value as SingleValue<DynamicFilterSelectOption>) ?? null}
          onChange={(v) => onChange(v as SingleValue<DynamicFilterSelectOption>)}
          options={field.options}
          styles={customSelectStyles<DynamicFilterSelectOption>()}
          isClearable={field.isClearable ?? true}
          placeholder={field.placeholder}
          className="min-w-[200px]"
          components={field.selectComponents}
        />
      );

    case "checkbox":
      return (
        <div className="flex items-center">
          <input
            type="checkbox"
            id={field.id}
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 text-indigo-600 rounded border-gray-300"
          />
          <label htmlFor={field.id} className="ml-2 block text-sm text-gray-700">
            {field.label}
          </label>
        </div>
      );

    case "text":
      return (
        <input
          type="text"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );

    case "number":
      return (
        <input
          type="number"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          value={(value as number | string | undefined) ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = raw === "" ? undefined : Number(raw);
            onChange(Number.isNaN(parsed as number) ? undefined : parsed);
          }}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          step={field.step}
        />
      );

    case "dateRange": {
      const v = (value as DynamicFilterDateRangeValue) ?? {
        startDate: undefined,
        endDate: undefined,
        preset: undefined,
      };
      return (
        <DateRangePickerButton
          onDateRangeChange={(startDate, endDate) =>
            onChange({ ...v, startDate, endDate })
          }
          initialStartDate={v.startDate}
          initialEndDate={v.endDate}
          initialPreset={v.preset}
          onPresetChange={(preset: IDateRangePreset | undefined) => {
            field.onPresetChange?.(preset);
            onChange({ ...v, preset: preset?.id as TDateRangePresetId | undefined });
          }}
        />
      );
    }

    default:
      return null;
  }
} 